// Assign Shifts - allocate a shift to one or more employees. Reads the
// active-employee list joined against emp_shift so every row shows its
// current shift (or a red "No shift" pill when unset).
//
// UX:
//   • Filters: search (name / emp_id / department), Department, Location,
//     Shift-Status ("Any", "No shift assigned", or a specific shift).
//     Department/Location options come from the loaded data - no extra fetch.
//   • Table has a fixed max-height and its own vertical scroll with a
//     sticky header. Header checkbox selects the CURRENTLY VISIBLE
//     (filtered) rows so users can filter → select-all → assign.
//   • Row click anywhere toggles the checkbox (except on links).
//   • Sticky bottom action bar: selected count, shift dropdown, Assign +
//     Remove buttons. Remove asks for confirmation.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import ScrollSelect from "./ScrollSelect";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Assign_Shifts.css";

function fmtTime12(hhmm) {
    if (!hhmm) return "";
    const [Hs, Ms] = hhmm.split(":");
    let H = parseInt(Hs, 10);
    const ampm = H >= 12 ? "PM" : "AM";
    H = H % 12; if (H === 0) H = 12;
    return `${H}:${Ms} ${ampm}`;
}

function Assign_Shifts({ navSize }) {
    const [shifts, setShifts]         = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [selected, setSelected]     = useState(new Set());
    const [pickedShift, setPickedShift] = useState("");
    const [busy, setBusy]             = useState(false);

    // Filters
    const [search, setSearch]     = useState("");
    const [deptFilter, setDept]   = useState("");
    const [locFilter, setLoc]     = useState("");
    const [statusFilter, setStat] = useState("any"); // "any" | "none" | shift_id (string)

    const navigate = useNavigate();

    useEffect(() => {
        fetchLogged();
        Promise.all([fetchShifts(), fetchAllocations()]).finally(() => setLoading(false));
    }, []);

    const fetchLogged = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login");
            }
        } catch (err) { console.error("logined_employee:", err); }
    };

    const fetchShifts = async () => {
        try {
            const res = await apiFetch("/api/shift/list", { credentials: "include" });
            const data = await res.json();
            setShifts(Array.isArray(data) ? data : []);
        } catch { setShifts([]); }
    };
    const fetchAllocations = async () => {
        try {
            const res = await apiFetch("/api/shift/allocations", { credentials: "include" });
            const data = await res.json();
            setAllocations(Array.isArray(data) ? data : []);
        } catch { setAllocations([]); }
    };

    // Distinct departments / locations - derived from allocations, not a
    // separate fetch (the list already has everything we need).
    const departments = useMemo(
        () => [...new Set(allocations.map(a => a.department_name).filter(Boolean))].sort(),
        [allocations]
    );
    const locations = useMemo(
        () => [...new Set(allocations.map(a => a.emp_location).filter(Boolean))].sort(),
        [allocations]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allocations.filter(r => {
            if (deptFilter && r.department_name !== deptFilter) return false;
            if (locFilter  && r.emp_location    !== locFilter)  return false;
            if (statusFilter === "none" && r.shift_id) return false;
            if (statusFilter !== "any" && statusFilter !== "none") {
                if (String(r.shift_id || "") !== String(statusFilter)) return false;
            }
            if (q) {
                const hay = `${r.emp_name || ""} ${r.emp_id || ""} ${r.department_name || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [allocations, search, deptFilter, locFilter, statusFilter]);

    const noShiftCount = useMemo(
        () => allocations.filter(r => !r.shift_id).length,
        [allocations]
    );

    // Shift dropdown options for the action bar: "Name (10:00 AM - 6:00 PM)".
    const shiftOpts = useMemo(
        () => shifts.map(s => ({
            value: String(s.shift_id),
            label: `${s.shift_name} (${fmtTime12(s.start_time)} - ${fmtTime12(s.end_time)})`,
        })),
        [shifts]
    );

    // Header checkbox = are ALL currently-visible rows selected?
    const filteredIds = useMemo(() => filtered.map(r => r.emp_id), [filtered]);
    const allVisibleSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));

    const toggleRow = (empId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(empId)) next.delete(empId);
            else next.add(empId);
            return next;
        });
    };
    const toggleAllVisible = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (allVisibleSelected) filteredIds.forEach(id => next.delete(id));
            else filteredIds.forEach(id => next.add(id));
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());

    const handleAssign = async () => {
        if (selected.size === 0) {
            dnAlert.warning("No employees selected", "Tick at least one employee first.");
            return;
        }
        if (!pickedShift) {
            dnAlert.warning("No shift picked", "Choose a shift from the dropdown.");
            return;
        }
        setBusy(true);
        try {
            const res = await apiFetch("/api/shift/allocate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ emp_ids: [...selected], shift_id: Number(pickedShift) }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Assign failed", data?.error || "Could not assign shift.");
                return;
            }
            const label = shifts.find(s => String(s.shift_id) === String(pickedShift))?.shift_name || "shift";
            dnAlert.success("Assigned", `${data.affected || selected.size} employee(s) assigned to "${label}".`);
            clearSelection();
            fetchAllocations();
        } catch (err) {
            console.error("assign:", err);
            dnAlert.error("Error", "Network error.");
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async () => {
        if (selected.size === 0) {
            dnAlert.warning("No employees selected", "Tick at least one employee first.");
            return;
        }
        const ok = await dnAlert.confirm({
            title: "Remove shift?",
            message: `Remove the current shift from ${selected.size} employee(s)? They will show "No shift" until reassigned.`,
            danger: true,
            confirmLabel: "Remove",
        });
        if (!ok) return;
        setBusy(true);
        try {
            const res = await apiFetch("/api/shift/unallocate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ emp_ids: [...selected] }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Remove failed", data?.error || "Could not remove shift.");
                return;
            }
            dnAlert.success("Removed", `${data.affected || selected.size} employee(s) no longer have a shift assigned.`);
            clearSelection();
            fetchAllocations();
        } catch (err) {
            console.error("remove:", err);
            dnAlert.error("Error", "Network error.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="as-container">
            <div className={`as-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                        </a>
                        Assign Shifts
                    </h2>
                    <p className="dspr-subtitle">
                        Assign a shift to employees. Filter by department or location for bulk allocation, tick the employees, pick a shift and assign.
                        {noShiftCount > 0 && (
                            <> <strong className="as-inline-warn">{noShiftCount} employee{noShiftCount === 1 ? "" : "s"} have no shift yet.</strong></>
                        )}
                    </p>
                </div>

                <div className="as-filter-row">
                    <div className="as-search-wrap">
                        <span className="as-search-icon">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="7" cy="7" r="5" stroke="#94a3b8" strokeWidth="1.5"/>
                                <line x1="11" y1="11" x2="14" y2="14" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </span>
                        <input
                            className="as-search"
                            type="text"
                            placeholder="Search name, employee ID, department…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select className="as-select" value={deptFilter} onChange={(e) => setDept(e.target.value)}>
                        <option value="">All Departments</option>
                        {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className="as-select" value={locFilter} onChange={(e) => setLoc(e.target.value)}>
                        <option value="">All Locations</option>
                        {locations.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select className="as-select" value={statusFilter} onChange={(e) => setStat(e.target.value)}>
                        <option value="any">Any Shift Status</option>
                        <option value="none">No shift assigned</option>
                        <optgroup label="Specific shift">
                            {shifts.map(s => (
                                <option key={s.shift_id} value={s.shift_id}>{s.shift_name}</option>
                            ))}
                        </optgroup>
                    </select>
                </div>

                <div className="as-card">
                    <div className="as-counters">
                        <span>{loading ? "Loading…" : `${filtered.length} of ${allocations.length} employees shown`}</span>
                    </div>

                    <div className="as-table-wrap">
                        <table className="as-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            onChange={toggleAllVisible}
                                            aria-label="Select all filtered employees"
                                        />
                                    </th>
                                    <th>Employee</th>
                                    <th>Employee ID</th>
                                    <th>Department</th>
                                    <th>Location</th>
                                    <th>Current Shift</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="as-empty">Loading…</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={6} className="as-empty">No employees match the current filters.</td></tr>
                                ) : filtered.map(row => {
                                    const checked = selected.has(row.emp_id);
                                    return (
                                        <tr
                                            key={row.emp_id}
                                            className={checked ? "as-row-selected" : ""}
                                            onClick={() => toggleRow(row.emp_id)}
                                        >
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleRow(row.emp_id)}
                                                    aria-label={`Select ${row.emp_name}`}
                                                />
                                            </td>
                                            <td className="as-name-cell">{row.emp_name || "-"}</td>
                                            <td><span className="as-emp-id">{row.emp_id}</span></td>
                                            <td>{row.department_name || "-"}</td>
                                            <td>{row.emp_location || "-"}</td>
                                            <td>
                                                {row.shift_id ? (
                                                    <span className="as-shift-pill as-shift-pill--on">{row.shift_name}</span>
                                                ) : (
                                                    <span className="as-shift-pill as-shift-pill--off">No shift</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ── Sticky action bar ────────────────────────────────── */}
                <div className="as-actionbar">
                    <div className="as-actionbar-count">
                        <strong>{selected.size}</strong> selected
                        {selected.size > 0 && (
                            <button className="as-linkbtn" onClick={clearSelection}>clear</button>
                        )}
                    </div>
                    <div className="as-actionbar-select">
                        <ScrollSelect
                            value={pickedShift}
                            options={shiftOpts}
                            placeholder="Select a shift…"
                            onChange={setPickedShift}
                        />
                    </div>
                    <button className="as-btn as-btn-primary" onClick={handleAssign} disabled={busy}>
                        {busy ? "Working…" : "Assign to Shift"}
                    </button>
                    <button className="as-btn as-btn-danger" onClick={handleRemove} disabled={busy}>
                        Remove Shift
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Assign_Shifts;
